import { useState, useEffect } from 'react';
import './ActivityLog.css';

/**
 * Activity Log Component
 * Displays content change history - who changed what, when, and current status
 */

function ActivityLog({ apiBase }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterAction, setFilterAction] = useState('all');
  const [selectedContent, setSelectedContent] = useState(null);

  useEffect(() => {
    fetchActivities();
  }, [apiBase]);

  const fetchActivities = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiBase}/admin/update-history`, {
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) throw new Error('Failed to fetch activities');

      const data = await response.json();
      if (data.ok && Array.isArray(data.data)) {
        setActivities(data.data);
      }
    } catch (err) {
      console.error('Error fetching activities:', err);
    } finally {
      setLoading(false);
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      'create': 'Created',
      'update': 'Updated',
      'publish': 'Published',
      'approve': 'Approved',
      'reject': 'Rejected',
      'draft': 'Draft',
      'pending_review': 'Pending',
    };
    return labels[action] || action.replace(/_/g, ' ');
  };

  const getStatusColor = (status) => {
    const colors = {
      'published': 'status-published',
      'draft': 'status-draft',
      'pending': 'status-pending',
      'approved': 'status-approved',
      'rejected': 'status-rejected',
    };
    return colors[status] || '';
  };

  // Filter activities
  const filteredActivities = activities.filter(activity => {
    const matchesStatus = filterStatus === 'all' || activity.status_code === filterStatus;
    const matchesAction = filterAction === 'all' || activity.action === filterAction;
    return matchesStatus && matchesAction;
  });

  const uniqueActions = [...new Set(activities.map(a => a.action))];

  const RefreshIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="23 4 23 10 17 10"></polyline>
      <polyline points="1 20 1 14 7 14"></polyline>
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36M20.49 15a9 9 0 0 1-14.85 3.36"></path>
    </svg>
  );

  const handleViewContent = (activity) => {
    setSelectedContent(activity);
  };

  const handleCloseContent = () => {
    setSelectedContent(null);
  };

  return (
    <div className="activity-log">
      <div className="content-header">
        <h2 className="content-title">Activity Log</h2>
        <p className="content-subtitle">Track all content changes and approvals</p>
      </div>

      {/* Filters */}
      <div className="log-filters">
        <select 
          value={filterAction} 
          onChange={(e) => setFilterAction(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Actions</option>
          {uniqueActions.map(action => (
            <option key={action} value={action}>
              {getActionLabel(action)}
            </option>
          ))}
        </select>

        <select 
          value={filterStatus} 
          onChange={(e) => setFilterStatus(e.target.value)}
          className="filter-select"
        >
          <option value="all">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="pending">Pending Review</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        <button className="btn-refresh" onClick={fetchActivities} title="Refresh">
          <RefreshIcon />
        </button>
      </div>

      {/* Activities Table */}
      {loading ? (
        <div className="loading">Loading activities...</div>
      ) : filteredActivities.length === 0 ? (
        <div className="empty-state">
          <p>No activities found</p>
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="activities-table">
            <thead>
              <tr>
                <th>Content Title</th>
                <th>Action</th>
                <th>Status</th>
                <th>Changed By</th>
                <th>Date & Time</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredActivities.map(activity => (
                <tr key={activity.id}>
                  <td data-label="Content">
                    <span className="content-title-cell">{activity.content_title || '-'}</span>
                  </td>
                  <td data-label="Action">
                    <span className="action-label">{getActionLabel(activity.action)}</span>
                  </td>
                  <td data-label="Status">
                    <span className={`status-badge ${getStatusColor(activity.status_code)}`}>
                      {activity.status_code}
                    </span>
                  </td>
                  <td data-label="Changed By">
                    <span className="actor-name">{activity.actor_name || '-'}</span>
                  </td>
                  <td data-label="Date & Time">
                    <span className="datetime">
                      {new Date(activity.edited_at).toLocaleDateString()} {new Date(activity.edited_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </td>
                  <td>
                    <button 
                      className="btn-view-content"
                      onClick={() => handleViewContent(activity)}
                      title="View content details"
                    >
                      View Content
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Content Details Modal */}
      {selectedContent && (
        <div className="modal-overlay" onClick={handleCloseContent}>
          <div className="modal-dialog content-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Content Details</h3>
              <button className="modal-close" onClick={handleCloseContent}>×</button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <label>Title:</label>
                <span>{selectedContent.content_title || '-'}</span>
              </div>
              <div className="detail-row">
                <label>Action:</label>
                <span>{getActionLabel(selectedContent.action)}</span>
              </div>
              <div className="detail-row">
                <label>Status:</label>
                <span className={`status-badge ${getStatusColor(selectedContent.status_code)}`}>
                  {selectedContent.status_code}
                </span>
              </div>
              <div className="detail-row">
                <label>Changed By:</label>
                <span>{selectedContent.actor_name || '-'}</span>
              </div>
              <div className="detail-row">
                <label>Date & Time:</label>
                <span>{new Date(selectedContent.edited_at).toLocaleString()}</span>
              </div>
              {selectedContent.note && (
                <div className="detail-row">
                  <label>Changes Made:</label>
                  <span className="note-content">{selectedContent.note}</span>
                </div>
              )}
              <div className="detail-row">
                <label>Content Slug:</label>
                <span className="slug">{selectedContent.content_slug || '-'}</span>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={handleCloseContent}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ActivityLog;
